import { request } from '@/api/client';
import type {
  RateMessageRequest,
  RateMessageResponse,
  LikeStatusResponse,
  EchoLikeStatsResponse,
  PopularEchoesResponse,
  MessageLikesListResponse,
  UserLikedMessagesResponse,
  SubmitFeedbackRequest,
  SubmitFeedbackResponse,
} from '@/types/ratings';

/** Message rating + feedback endpoints — like, dislike, super-like, follow-up feedback, popularity stats. */
export const ratingsService = {
  /**
   * Rate a message (Like, Dislike, Super Like).
   */
  rateMessage: (data: RateMessageRequest): Promise<RateMessageResponse> =>
    request<RateMessageResponse>('/message-likes/rate', {
      method: 'POST',
      body: JSON.stringify(data),
      auth: true,
    }),

  getLikeStatus: (messageId: string): Promise<LikeStatusResponse> =>
    request<LikeStatusResponse>(`/message-likes/message/${encodeURIComponent(messageId)}/status`, {
      method: 'GET',
      auth: true,
    }),

  getEchoStats: (echoId: string): Promise<EchoLikeStatsResponse> =>
    request<EchoLikeStatsResponse>(`/message-likes/echo/${encodeURIComponent(echoId)}/stats`, {
      method: 'GET',
      auth: false,
    }),

  /**
   * Fetch stats for many echoes in one request — replaces the per-echo
   * fanout on list pages.
   */
  getEchoesStatsBatch: (echoIds: string[]): Promise<Record<string, EchoLikeStatsResponse>> => {
    const params = new URLSearchParams();
    echoIds.forEach((id) => params.append('ids', id));
    return request<Record<string, EchoLikeStatsResponse>>(
      `/message-likes/echoes/stats?${params.toString()}`,
      {
        method: 'GET',
        auth: false,
      }
    );
  },

  getPopularEchoes: (limit = 10, minMessages = 10): Promise<PopularEchoesResponse> =>
    request<PopularEchoesResponse>(
      `/message-likes/popular?limit=${limit}&min_messages=${minMessages}`,
      {
        method: 'GET',
        auth: false,
      }
    ),

  getMessageLikes: (messageId: string): Promise<MessageLikesListResponse> =>
    request<MessageLikesListResponse>(
      `/message-likes/message/${encodeURIComponent(messageId)}/likes`,
      {
        method: 'GET',
        auth: false,
      }
    ),

  getMyLikesForEcho: (echoId: string): Promise<UserLikedMessagesResponse> =>
    request<UserLikedMessagesResponse>(
      `/message-likes/echo/${encodeURIComponent(echoId)}/my-likes`,
      {
        method: 'GET',
        auth: true,
      }
    ),

  submitFeedback: (data: SubmitFeedbackRequest): Promise<SubmitFeedbackResponse> =>
    request<SubmitFeedbackResponse>('/message-likes/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
      auth: true,
    }),
};
